import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== null) navigate("/admin");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-background">
      <div className="hidden md:flex relative bg-foreground text-background items-center justify-center overflow-hidden">
        <span className="font-deva absolute text-primary/20 text-[38vw] leading-none select-none">
          न
        </span>
        <div className="relative z-10 px-16">
          <p className="font-deva text-primary text-3xl mb-4">नवजीवन</p>
          <h1 className="font-display font-extrabold text-4xl tracking-tight leading-tight">
            Institute<br />Content Studio
          </h1>
          <p className="mt-4 font-body text-background/60 max-w-xs">
            Manage courses, testimonials, contact details and enquiries.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm"
        >
          <span className="overline text-primary">Admin access</span>
          <h2 className="mt-4 font-display font-bold text-3xl tracking-tight mb-10">
            Sign in
          </h2>

          <div className="space-y-5">
            <div>
              <Label className="font-body text-sm mb-2 block">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@navjeevan.com"
                className="h-12"
              />
            </div>
            <div>
              <Label className="font-body text-sm mb-2 block">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12"
              />
            </div>
          </div>

          <div className="mt-3 text-right">
            <Link
              to="/admin/forgot-password"
              data-testid="forgot-password-link"
              className="font-body text-sm text-muted-foreground underline"
            >
              Forgot password?
            </Link>
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-8 w-full h-12 rounded-full bg-foreground text-background hover:bg-primary"
          >
            {loading ? "Signing in..." : "Sign in →"}
          </Button>
        </motion.form>
      </div>
    </div>
  );
}
